import { LoadingOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Flex, Modal, Spin, Typography } from "antd";
import CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useForm as useHookForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useParams, useSearchParams } from "react-router-dom";
import { CustomerServiceApi } from "src/api";
import AuthApi from "src/api/webapp/auth-api";
import { ExtendedGradientLogo, LogoWhiteSimple } from "src/assets/images";
import { Input } from "src/components";
import GradientBackground from "src/components/GradientBackground";
import AccessBlockByAccounting from "src/components/Modal/AccessBlockByAccounting";
import GoodErrorMessage from "src/components/Modal/GoodErrorMessage";
import { REACT_APP_KEY_ENCRYPT_URL, REACT_APP_QRPOINT_ENDPOINT } from "src/constants";
import { useChat } from "src/contexts/chat.context";
import { useGlobalContext } from "src/contexts/global.context";
import { ChatOption } from "src/modules/ai-agent/enums";
import { Routes } from "src/routes";
import { Storage } from "src/services";
import { Cookie, ExpiredSessionError, dictionary, getCookieByKey, i18n, setCookie } from "src/utils";
import { CUSTOMER_SERVICE_FIELDS } from "src/utils/enums/customer-service.enum";
import { Form, LoginContainer, LoginLinkContainer, LoginWrapper, RedirectSystemWrapper, SignInButton } from "./styles";
import { loginSchema } from "./validation";
const { Title, Text, Link, Paragraph } = Typography;

type Props = {
  auth: (credentials: Credentials) => void;
};

function Login({ auth }: Props): JSX.Element {
  const params = useParams();
  const [searchParams, _] = useSearchParams();
  const yappToken = searchParams.get("yappToken");
  const navigate = useNavigate();
  const { setChatToken, setLite, setOption, setEmail } = useChat();

  const { setIsYappUser } = useGlobalContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useHookForm<Credentials>({
    resolver: yupResolver(loginSchema),
  });

  const [cookieVisible, setCookieVisible] = useState(false);

  const generateChatToken = (companyCode?: string, userId?: string) => {
    let sessionIdCookie = getCookieByKey(Cookie.QRSession);
    if (!sessionIdCookie) {
      sessionIdCookie = crypto.randomUUID();
      setCookie(Cookie.QRSession, sessionIdCookie, 60);
    }
    AuthApi.generateSessionToken(sessionIdCookie, companyCode || sessionIdCookie, userId || sessionIdCookie)
      .then((sharedToken) => {
        if (sharedToken) {
          setChatToken(sharedToken);
          if (companyCode) {
            setLite(false);
          }
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onHandleSubmit = handleSubmit(async (data) => {
    await checkLogin(data);
  });

  async function checkLogin(data: Credentials) {
    try {
      await auth(data);
      // O identificador do usuário não é atributo de empresa: o backend já o registra como
      // zorya_user_id a partir do usuário autenticado. Enviá-lo aqui fazia a chamada inteira
      // falhar, e nem nome nem e-mail eram atualizados.
      CustomerServiceApi.update({
        [CUSTOMER_SERVICE_FIELDS.NAME]: Storage.getUserName(),
        [CUSTOMER_SERVICE_FIELDS.EMAIL]: Storage.getUserEmail(),
      });

      setEmail(Storage.getUserEmail());
      generateChatToken(Storage.getCompanyCode(), Storage.getUserId().toString());

      navigate(Routes.HOME);
    } catch (error) {
      console.error(error);
      if (!(error instanceof ExpiredSessionError)) {
        GoodErrorMessage({
          title: i18n(dictionary.error.LOGIN_ERROR.title),
          message: i18n(dictionary.error.LOGIN_ERROR.message),
          onOk: () => checkLogin(data),
          onClickLink: () => setOption(ChatOption.CHAT),
        });
      }
      if (error.message === "block_by_accounting") {
        AccessBlockByAccounting();
      }
      console.error(error);
    }
  }

  const acceptCookies = () => {
    setCookie(Cookie.Accepted, "true", 26640);
    setCookieVisible(false);
    generateChatToken();
  };

  async function autoLogin(fromYapp: boolean, data: any) {
    if (fromYapp) {
      await checkLogin({ email: "", password: "", yappToken: data });
    } else {
      if (REACT_APP_KEY_ENCRYPT_URL.length) {
        const decryptedData = CryptoJS.AES.decrypt(data, REACT_APP_KEY_ENCRYPT_URL).toString(CryptoJS.enc.Utf8);
        if (decryptedData !== String(Storage.getUserId())) {
          await checkLogin({ email: "", password: "", userId: decodeURIComponent(data) });
        }
      }
    }
    navigate("/");
  }

  useEffect(() => {
    if (yappToken) {
      //Login Yapp
      autoLogin(true, yappToken);
      setIsYappUser(true);
    } else if (params && params.data) {
      //Login automático
      autoLogin(false, params.data);
    } else {
      //Login manual
      const hasCookie = getCookieByKey(Cookie.Accepted) === "true";

      if (!hasCookie) {
        setCookieVisible(true);
      }
    }
  }, []);

  if (yappToken || (params && params.data)) {
    return (
      <Flex
        style={{ height: "100vh", width: "100vw" }}
        justify="center"
        align="center"
      >
        <Spin
          size="large"
          indicator={<LoadingOutlined />}
        />
      </Flex>
    );
  }

  return params.data ? (
    <RedirectSystemWrapper>
      <img
        src={LogoWhiteSimple}
        alt="Logo"
        width="80px"
        style={{ position: "fixed", marginBottom: 50 }}
      />
      <Spin
        style={{ color: "#FFF" }}
        indicator={<LoadingOutlined style={{ fontSize: 150 }} />}
      />
      <span style={{ color: "#FFF", marginTop: 30, fontSize: 18, textAlign: "center" }}>
        Quase lá, estamos te enviando para o QRPoint: Admissão digital...
      </span>
    </RedirectSystemWrapper>
  ) : (
    <GradientBackground>
      <LoginWrapper>
        <LoginContainer>
          <h1>
            <img
              src={ExtendedGradientLogo}
              alt="Logo"
              width="100px"
            />
          </h1>
          <h2>Acesse usando seu email e senha</h2>
          <Form
            onSubmit={onHandleSubmit}
            autoComplete="on"
          >
            <Input
              name="email"
              register={register("email")}
              placeholder="E-mail"
              error={errors.email}
            />
            <Input
              name="password"
              register={register("password", { required: true })}
              placeholder="Senha"
              error={errors.password}
              type="password"
            />
            <Flex
              justify="flex-end"
              style={{ width: "100%" }}
            >
              <Text
                className="custom-link"
                type="secondary"
                style={{
                  fontSize: "8pt",
                  cursor: "pointer",
                  margin: "3px 3px 0 0",
                }}
                onClick={() => navigate(Routes.FORGOT_PASSWORD, { replace: true })}
              >
                {i18n(dictionary.pages.login.fields.forgotPassword)}
              </Text>
            </Flex>
            <SignInButton type="submit">{i18n(dictionary.pages.login.fields.signIn)}</SignInButton>
          </Form>
        </LoginContainer>
      </LoginWrapper>
      <Modal
        open={cookieVisible}
        footer={null}
        closable={false}
        centered
        className="login-cookie-modal"
      >
        <Title level={4}>{i18n(dictionary.aboutCookies)}</Title>
        <Paragraph>{i18n(dictionary.aboutCookiesUse)}</Paragraph>
        <Paragraph>{i18n(dictionary.aboutCookiesShare)}</Paragraph>
        <Paragraph>{i18n(dictionary.aboutCookiesClick)} </Paragraph>
        <Button
          type="primary"
          block
          onClick={acceptCookies}
        >
          {i18n(dictionary.aboutCookiesAccept)}
        </Button>
        <LoginLinkContainer>
          <Link
            href={`${REACT_APP_QRPOINT_ENDPOINT}/privacidade`}
            target="_blank"
          >
            {i18n(dictionary.cookiesPrivacyPolicy)}
          </Link>
        </LoginLinkContainer>
      </Modal>
    </GradientBackground>
  );
}

export default Login;
